import { useMemo } from 'react';
import { Group, Text } from '@repletoryreach/sdk-ui';
import { SegmentedControl } from '@repletoryreach/sdk-ui/inputs';
import { usePersistedState } from '../lib/usePersistedState';
import { formatMinor } from '../lib/format';
import { useT } from '../i18n/I18nProvider';

/**
 * Which currency the money cards on a billing page show.
 *
 * A platform with tenants in rand, naira and shillings has no single total:
 * adding cents of one currency to cents of another gives a number that means
 * nothing. The cards used to print every currency's figure joined on one
 * line, which overflowed the card the moment there were three. Now they show
 * one currency, chosen here and remembered across the billing pages, with the
 * others one click away.
 *
 * The choice survives only while it still applies: a remembered currency that
 * the current window does not contain falls back to the first one passed, so
 * pass the currencies in order of weight (most entries, most revenue) and the
 * busiest one is what a first visit sees.
 */
export function useDisplayCurrency(available: Iterable<string>): [string | null, (next: string) => void] {
  const [stored, setStored] = usePersistedState<string | null>('admin.billing.currency', null);
  const key = Array.from(available).join(',');
  const list = useMemo(() => Array.from(new Set(key ? key.split(',') : [])), [key]);
  const effective = stored && list.includes(stored) ? stored : (list[0] ?? null);
  return [effective, setStored];
}

/** The figure for one currency out of a per-currency map, or empty when that currency has none. */
export function amountIn(byCurrency: Map<string, number>, currency: string | null): string {
  if (!currency || !byCurrency.has(currency)) return '';
  return formatMinor(byCurrency.get(currency)!, currency);
}

/**
 * The switch itself. Renders nothing with fewer than two currencies, because a
 * choice of one is not a choice.
 */
export function CurrencySwitch({ currencies, value, onChange }: {
  currencies: string[];
  value: string | null;
  onChange: (next: string) => void;
}) {
  const t = useT();
  const sorted = useMemo(() => Array.from(new Set(currencies)).sort(), [currencies]);
  if (sorted.length < 2 || !value) return null;
  return (
    <Group gap="sm" align="center" wrap="nowrap" className="rr-currency-switch" role="group" aria-label={t('billing.currency.aria')}>
      <Text role="bodySmall" fw={600} c="var(--rr-color-text-muted)">{t('billing.currency.showIn')}</Text>
      <SegmentedControl
        size="xs"
        value={value}
        onChange={onChange}
        data={sorted.map((c) => ({ value: c, label: c }))}
      />
    </Group>
  );
}
